import React, { CSSProperties, useMemo } from "react";
import styled, { css } from "styled-components";
import { ImageVersions } from "@domain/media/dtos";
import { useSourceLoaded } from "@domain/media/hooks";
import { Media } from "@domain/media/media";
import { mobile } from "@ui/styles/utils";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";

interface ProfileBannerProps extends React.HTMLAttributes<HTMLElement> {
	picture?: Media;
	big?: boolean;
	defaultUrl?: string;
	shadowed?: boolean;
	height?: number;
	containerStyle?: CSSProperties;
}
export const ProfileBanner: React.FC<ProfileBannerProps> = ({
	picture,
	big,
	defaultUrl,
	height,
	children,
	containerStyle,
	...htmlProps
}) => {
	const finalUrl = useMemo(
		() =>
			defaultUrl
				? defaultUrl
				: picture
					? picture.versions
						? picture.versions[ImageVersions.BANNER]
						: picture.url
					: undefined,
		[picture, defaultUrl]
	);

	const sourceLoaded = useSourceLoaded(finalUrl);

	return (
		<Container
			{...htmlProps}
			big={big}
			height={height}
			url={
				!finalUrl
					? undefined
					: !sourceLoaded && picture?.metadata?.placeholder
						? picture.metadata.placeholder
						: sourceLoaded
							? sourceLoaded
							: undefined
			}
			hasBorder={!finalUrl}
			style={containerStyle ? containerStyle : undefined}
			personalizedRadius={!!containerStyle}
		>
			{children}
		</Container>
	);
};

const Container = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => !["url", "big", "height", "shadowed", "hasBorder"].includes(prop),
})<{
	url?: string;
	big?: boolean;
	height?: number;
	shadowed?: boolean;
	hasBorder?: boolean;
}>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: ${({ height, big }) => (height ? height : big ? 220 : 110)}px;

	${({ shadowed, hasBorder }) => css`
		${shadowed && "box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.2)"};
		border-width: ${hasBorder ? 1 : 0}px;
	`}

	@media ${mobile} {
		height: ${({ height, big }) => (height ? height : big ? 140 : 110)}px;
	}

	${({ url }) =>
		url &&
		css`
			background-color: transparent;
			background-repeat: no-repeat;
			background-image: url(${url});
			background-position: 50% 50%;
			background-size: cover;
		`}
`;
